import type { FcAdminTarget } from '@shared/fcAdminApiTypes';
import { normalizeFcAdminTarget } from '@/lib/fcAdminTarget';

type RecordValue = Record<string, unknown>;

export type ActionCharacterOption = {
    value: string;
    label: string;
    name: string;
    target: FcAdminTarget;
};

const records = (value: unknown) => Array.isArray(value)
    ? value.filter((entry): entry is RecordValue => !!entry && typeof entry === 'object' && !Array.isArray(entry))
    : [];

const text = (value: unknown, fallback: string) => typeof value === 'string' && value.trim()
    ? value.trim()
    : fallback;

const optionFor = (
    source: RecordValue,
    publicId: number,
    value: string,
    allowOffline: boolean,
): ActionCharacterOption | undefined => {
    const target = normalizeFcAdminTarget(source.target ?? source, publicId);
    if (!target || !allowOffline && !target.connectionGeneration) return undefined;
    const name = text(source.name ?? source.rpName, `FC-ID ${publicId}`);
    const job = text(source.jobLabel ?? source.jobName ?? source.job ?? source.groupLabel, 'Charakter');
    const status = target.connectionGeneration ? 'Online' : 'Offline';
    return { value, label: `${name} · ${job} · ${status}`, name, target };
};

export const buildActionCharacterOptions = (
    matching: RecordValue[],
    publicId: number,
    allowOffline: boolean,
): ActionCharacterOption[] => {
    const options: ActionCharacterOption[] = [];
    for (const [accountIndex, account] of matching.entries()) {
        const characters = records(account.characters);
        if (characters.length) {
            for (const [characterIndex, character] of characters.entries()) {
                const slot = Number(character.slot);
                const identity = Number.isSafeInteger(slot) && slot >= 0
                    ? `account:${accountIndex}:slot:${slot}`
                    : `account:${accountIndex}:character:${characterIndex}:${text(character.name ?? character.rpName, 'unknown')}`;
                const option = optionFor(character, publicId, identity, allowOffline);
                if (option) options.push(option);
            }
            continue;
        }
        const option = optionFor(account, publicId, `account:${accountIndex}:selected`, allowOffline);
        if (option) options.push(option);
    }
    return options;
};

export const formatActionConfirmationTarget = (publicId: number | undefined, characterName?: string) => {
    if (publicId === undefined) return 'Serverweit';
    return characterName ? `FC-ID ${publicId} · Charakter: ${characterName}` : `FC-ID ${publicId}`;
};
